//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 04/09/2024
// Version       : 1
// Description   : Revision de las tareas a realizar para un vehículo
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   : 28/01/2025
// Change by     : Guillermo Sedran
// Change description: Agregado filtro en base a frecuencia en km + cambios en la logica del guardado y la muestra de las tareas
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import React, { useState, useEffect, useRef, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Divider,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Checkbox,
  Stack,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import KilometrajeTareasRevision from "./KilometrajeTareasRevision";

//ADJUNTAR ARCHIVOS
import FileUploadTareas from "../FileUploadTareas";

// ICONOS
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Notification
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// DAYJS
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localeData from "dayjs/plugin/localeData";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isoWeek from "dayjs/plugin/isoWeek"; // Usar semanas en estándar ISO (lunes = 1, Domingo = 7)

import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.locale("es");

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function TareasRevision(props) {
  const [cargado, setCargado] = useState(false); // Estado para evitar carga prematura
  const [kilometrajeUltimaRevision, setKilometrajeUltimaRevision] =
    useState("");

  const [modoEdicion, setModoEdicion] = useState(false);
  const [tareasAgrupadas, setTareasAgrupadas] = useState({});
  const [vehiculo, setVehiculo] = useState(props.vehiculo);
  const [confirmarVolverDialogOpen, setConfirmarVolverDialogOpen] =
    useState(false); // Estado para el diálogo de confirmación de "Volver"

  const [tareasXtipo, setTareasXtipo] = useState([]);
  const [fechaUltimaRevision, setFechaUltimaRevision] = useState(null);
  const [fechaRevisionAnterior, setFechaRevisionAnterior] = useState(null);
  const [ultimaRevision, setUltimaRevision] = useState(null);

  const [observacionTemp, setObservacionTemp] = useState({});

  // Dialogo para confirmar la eliminacion de datos al deshabilitar una parte de vehiculos con datos
  const [openDisableDialog, setOpenDisableDialog] = useState(false);
  const [pendingCategory, setPendingCategory] = useState(null);

  // ------------------------------------------------

  // Activar/Desactivar categorias de Tareas
  const [enabledCategories, setEnabledCategories] = useState({});
  const categoriesInitialized = useRef(false);

  useEffect(() => {
    const partes = Object.keys(tareasAgrupadas);
    // Si aún no inicialicé y hay al menos una categoría:
    if (!categoriesInitialized.current && partes.length > 0) {
      const inicial = partes.reduce((acc, parte) => {
        acc[parte] = false;
        return acc;
      }, {});
      setEnabledCategories(inicial);
      categoriesInitialized.current = true;
    }
  }, [tareasAgrupadas]);

  // console.log("enabledCategories: ", enabledCategories);

  // ------------------------------------------------

  // Función para limpiar y desactivar
  const actuallyDisableCategory = (parte) => {
    // Limpiar observaciones y aprobado
    const ids = (tareasAgrupadas[parte] || []).map((t) => t.id_tarea);
    setObservacionTemp((obsPrev) => {
      const next = { ...obsPrev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
    setTareasXtipo((tasksPrev) =>
      tasksPrev.map((t) =>
        ids.includes(t.id_tarea) ? { ...t, aprobado: 0, observacion: "" } : t
      )
    );
    // Desactivar categoría
    setEnabledCategories((prev) => ({ ...prev, [parte]: false }));
  };

  // ------------------------------------------------

  const toggleCategory = (parte) => {
    const isCurrentlyEnabled = enabledCategories[parte];
    if (isCurrentlyEnabled) {
      // Chequear si hay tareas completas
      const tareas = tareasAgrupadas[parte] || [];
      const hasCompletedData = tareas.some(
        (t) =>
          t.aprobado === 1 ||
          observacionTemp[t.id_tarea]?.trim() !== "" ||
          t.observacion?.trim() !== ""
      );
      if (hasCompletedData) {
        // Mostrar diálogo de confirmación
        setPendingCategory(parte);
        setOpenDisableDialog(true);
      } else {
        // No hay datos, se puede deshabilitar directamente
        actuallyDisableCategory(parte);
      }
    } else {
      // Se está habilitando, no hay peligro
      setEnabledCategories((prev) => ({ ...prev, [parte]: true }));
    }
  };

  // ------------------------------------------------

  // PARA DIALOG DE CONFIRMAR SALIDA DEL COMPONENTE
  const handleCloseConfirmarVolverDialog = () => {
    setConfirmarVolverDialogOpen(false);
  };

  const handleConfirmarVolverReturn = () => {
    handleCloseConfirmarVolverDialog();
    props.onClose(); // Cierra el componente al confirmar "Volver"
  };

  const handleReturnClick = () => {
    setConfirmarVolverDialogOpen(true); // Abre el diálogo de confirmación
  };

  // ------------------------------------------

  // handlers del diálogo
  const handleConfirmDisable = () => {
    actuallyDisableCategory(pendingCategory);
    setPendingCategory(null);
    setOpenDisableDialog(false);
  };
  const handleCancelDisable = () => {
    setPendingCategory(null);
    setOpenDisableDialog(false);
  };

  // ------------------------------------------

  const [kilometraje, setKilometraje] = useState(null);
  const [dialogoKilometraje, setDialogoKilometraje] = useState(false);
  const [kilometrajeConfirmado, setKilometrajeConfirmado] = useState(false);

  const handleCloseDialogKilometraje = () => {
    setDialogoKilometraje(false); // Cierra solo el diálogo del kilometraje
  };

  const handleConfirmKilometraje = async (nuevoKilometraje) => {
    setKilometraje(nuevoKilometraje); // Actualiza el kilometraje
    setDialogoKilometraje(false); // Cierra el diálogo del kilometraje
    setKilometrajeConfirmado(true); // Marca el kilometraje como confirmado
    await obtenerTareasXtipo(); // Llama a la función para obtener tareas
  };

  const handleCloseBothDialogs = () => {
    props.onClose(); // Cierra el componente
  };

  // ------------------

  const handleTempObservacionChange = (id, value) => {
    // Actualizar observacionTemp y tareasXtipo
    setObservacionTemp((prev) => ({ ...prev, [id]: value }));

    setTareasXtipo((prevTareas) =>
      prevTareas.map((tarea) =>
        tarea.id_tarea === id ? { ...tarea, observacion: value || "" } : tarea
      )
    );
  };

  // ------------------

  // ADJUNTOS
  const [openCloseAdj, setOpenCloseAdj] = useState(false); //ADJUNTAR COMPROBANTE
  const [disBtn, setDisBtn] = useState(false); // Estado para deshabilitar el botón
  const [disBtnGuardar, setDisBtnGuardar] = useState(false); // Estado para deshabilitar el botón

  const handleOpenCloseAdj = (value) => {
    setOpenCloseAdj(value);
  };

  // ------------------

  // Notificación
  const [openSnack, setOpenSnack] = React.useState(false);
  const [colormensaje, setColorMensaje] = useState("");
  const [mensaje, setMensaje] = useState("");

  // ------------------------------------------------

  // AL REALIZAR LA PRIMERA REVISION, SE ABRE UN DIALOG QUE PREGUNTA POR EL KILOMETRAJE ACTUAL DEL VEHICULO
  useEffect(() => {
    if (vehiculo && !props.registroHoy) {
      setDialogoKilometraje(true);
    }
  }, [vehiculo, props.registroHoy]);

  // ------------------------------------------------

  // VERIFICO AL INICIAR SI HUBO REGISTRO EN EL DIA PARA HABILITAR EL MODO EDICION
  useEffect(() => {
    // Obtener el KILOMETRAJE de la última revisión realizada
    obtenerKilometrajeUltimaRevision();
    // Obtener la FECHA de la última revisión realizada
    obtenerFechaUltimaRevision();

    if (vehiculo && !cargado) {
      if (props.registroHoy) {
        // Si ya existe registro hoy, se habilita la edición
        setModoEdicion(true);
      } else {
        setModoEdicion(false);
      }
      setCargado(true); // Marca el componente como cargado después de la evaluación
    }
  }, [vehiculo, props.registroHoy, cargado]);

  useEffect(() => {
    // Si entramos en modoEdicion y ya obtuvo fechaUltimaRevision
    if (!modoEdicion || !fechaUltimaRevision) {
      return;
    }

    // Extraigo “DD-MM-YYYY” para el endpoint
    const fechaSolo = dayjs(fechaUltimaRevision, "DD-MM-YYYY").format(
      "DD-MM-YYYY"
    );

    console.log(
      "Voy a pedir fecha_rev_ant para:",
      fechaSolo,
      " vehículo:",
      vehiculo.id
    );

    // Llamo al endpoint que devuelve la última fecha anterior a hoy (La nueva)
    fetch(
      `${UrlApi}/vehiculos_revision/fecha_revision_anterior/${vehiculo.id}/${fechaSolo}`
    )
      .then((res) => res.json())
      .then((rows) => {
        setFechaRevisionAnterior(rows[0]?.fecha ?? null);
      })
      .catch((err) => console.error("Error obteniendo fecha anterior:", err));
  }, [modoEdicion, fechaUltimaRevision, vehiculo.id]);

  useEffect(() => {
    if (cargado) {
      if (modoEdicion) {
        obtenerUltimaRevision(); // En modo edición, carga la última revisión
        console.log("MODO EDICION ACTIVADO: ", modoEdicion);
      } else {
        if (kilometraje !== null) {
          obtenerTareasXtipo(); // Solo obtiene tareas si hay un kilometraje confirmado
          console.log("SIN MODO EDICION: ", modoEdicion);
        }
      }
    }
  }, [cargado, modoEdicion, fechaUltimaRevision, kilometraje]);

  //*************************************************************************************************************************************/

  // Verifico si el botón de guardar se debe habilitar o no
  const isGuardarDisabled = useMemo(() => {
    // Obtengo las partes/categorías del vehículo activas
    const partesActivas = Object.entries(enabledCategories)
      .filter(([_, activo]) => activo)
      .map(([parte]) => parte);

    // Si no hay ninguna activa, deshabilita el botón de guardado
    if (partesActivas.length === 0) return true;

    // Si hay una parte activa:
    // Chequea cada tarea de cada parte activa
    return partesActivas.some((parte) => {
      const tareas = tareasAgrupadas[parte] || [];
      // Si hay alguna tarea que NO está aprobada Y sin observación, se bloquea el botón
      return tareas.some((t) => {
        const obs = (observacionTemp[t.id_tarea] ?? t.observacion)?.trim();
        return t.aprobado !== 1 && !obs;
      });
    });
  }, [enabledCategories, tareasAgrupadas, observacionTemp]);

  // ---------------------

  // ACTIVAR/DESACTIVAR CHECKBOX DE CADA TAREA
  // SEGUN EL ESTADO DEL CHECKBOX, SE HABILITA/DESHABILITA EL TEXTFIELD PARA AGREGAR UNA OBSERVACIÓN
  const handleCheckboxChange = (id) => {
    setTareasXtipo((prevTareas) =>
      prevTareas.map((tarea) => {
        if (tarea.id_tarea === id) {
          const nuevoAprobado = tarea.aprobado === 1 ? 0 : 1;

          if (nuevoAprobado === 1) {
            setObservacionTemp((prev) => ({ ...prev, [id]: "" })); // Limpiar estado temporal
            return { ...tarea, aprobado: nuevoAprobado, observacion: "" };
          }

          return { ...tarea, aprobado: nuevoAprobado };
        }
        return tarea;
      })
    );
  };

  //*************************************************************************************************************************************/

  // FECHA DE LA ULTIMA REVISION REALIZADA AL VEHÍCULO
  const obtenerFechaUltimaRevision = () => {
    fetch(`${UrlApi}/vehiculos_revision/fecha_ultima_revision/${vehiculo.id}`)
      .then((data) => data.json())
      .then((data) => {
        if (data[0]?.fecha) {
          setFechaUltimaRevision(data[0].fecha); // Guardar la fecha de la última revisión
        } else {
          setFechaUltimaRevision(null); // Si no hay revisiones, dejar en null
        }
      })
      .catch((error) =>
        console.error("Error obteniendo fecha última revisión:", error)
      );
  };

  // KILOMETRAJE DE LA ULTIMA REVISION REALIZADA AL VEHÍCULO
  const obtenerKilometrajeUltimaRevision = () => {
    fetch(
      `${UrlApi}/vehiculos_revision/kilometraje_ultima_revision/${vehiculo.id}`
    )
      .then((data) => data.json())
      .then((data) => {
        // Verificar que el kilometraje no sea null o undefined
        if (
          data[0]?.kilometraje !== null &&
          data[0]?.kilometraje !== undefined
        ) {
          setKilometrajeUltimaRevision(data[0].kilometraje); // Guardar el km de la última revisión

          console.log("data[0].kilometraje: ", data[0].kilometraje);
        } else {
          setKilometrajeUltimaRevision(null); // Si no hay revisiones, dejar en null
        }
      })
      .catch((error) =>
        console.error("Error obteniendo kilometraje última revisión:", error)
      );
  };

  //*************************************************************************************************************************************/

  // Función auxiliar para verificar si es la primera revisión
  const esPrimeraRevision = (fechaUltimaRevision) => {
    return fechaUltimaRevision === null; // La primera revisión depende únicamente de la ausencia de revisiones previas
  };

  // Estado de carga
  const [loading, setLoading] = useState(false);

  // TAREAS POR TIPO DE VEHÍCULO
  const obtenerTareasXtipo = async () => {
    setLoading(true); // Inicia la carga
    try {
      const response = await fetch(
        `${UrlApi}/tareas_tipo_vehiculos/tipo_vehiculo/${vehiculo.id_tipo}/vehiculo/${vehiculo.id}`
      );

      if (!response.ok) {
        throw new Error(`Error en la respuesta de la API: ${response.status}`);
      }

      const data = await response.json();

      if (esPrimeraRevision(fechaUltimaRevision)) {
        setTareasXtipo(data);
      } else {
        const tareasFiltradas = filtrarTareasPorFrecuencia(
          data,
          fechaUltimaRevision,
          kilometraje
        );
        setTareasXtipo(tareasFiltradas);
      }
    } catch (error) {
      console.error("Error obteniendo tareas por tipo:", error);
    } finally {
      setLoading(false); // Finaliza la carga
    }
  };

  // FILTRAR TAREAS AL CREAR CON UNA REVISION EXISTENTE EN UN DIA ANTERIOR
  // SE TIENEN EN CUENTA LAS FRECUENCIAS (en tiempo y distancia) DE CADA TAREA PARA MOSTRARLAS
  const filtrarTareasPorFrecuencia = (
    tareas,
    fechaUltimaRevision,
    kilometraje
  ) => {
    const fechaActual = new Date();

    return tareas.filter((tarea) => {
      const { frecuencia, valor_km } = tarea;

      // Si es la primera revisión del vehículo, mostrar la tarea
      if (!fechaUltimaRevision) {
        return true;
      }

      // Verificar tareas basadas en kilometraje
      if (valor_km) {
        const kilometrajeUltimo = kilometrajeUltimaRevision || 0; // Si es null, asumir 0

        const diferenciaKilometraje = kilometraje - kilometrajeUltimo;

        if (diferenciaKilometraje >= valor_km) {
          return true;
        }
      }

      // Verificar tareas basadas en tiempo
      if (frecuencia && fechaUltimaRevision) {
        try {
          // Convertir la fecha "DD-MM-YYYY HH:mm:ss" a "YYYY-MM-DD"
          const [dia, mes, anio] = fechaUltimaRevision.split(" ")[0].split("-");
          const fechaUltima = new Date(`${anio}-${mes}-${dia}`); // Crear la fecha en formato ISO (YYYY-MM-DD)

          // Verificar si la "fechaUltima" es válida
          if (isNaN(fechaUltima)) {
            console.error(`Fecha no válida: "${fechaUltimaRevision}"`);
            return false;
          }

          const diferenciaDias = Math.floor(
            (fechaActual - fechaUltima) / (1000 * 60 * 60 * 24)
          ); // Diferencia exacta en días, uso math.floor para redondear hacia abajo y obtener valores enteros

          // **Diaria (264): Siempre se incluye
          if (frecuencia === 264) {
            return true;
          }

          // **Semanal (265): Se incluye si pasaron 7 días o más
          if (frecuencia === 265 && diferenciaDias >= 7) {
            return true;
          }

          // **Quincenal (266): Se incluye si pasaron 14 días o más
          if (frecuencia === 266 && diferenciaDias >= 14) {
            return true;
          }

          // **Mensual (267): Se incluye si pasaron 30 días o más
          if (frecuencia === 267 && diferenciaDias >= 30) {
            return true;
          }
        } catch (error) {
          console.error(
            `Error procesando fechaUltimaRevision: "${fechaUltimaRevision}"`,
            error
          );
        }
      }

      return false; // No cumple ninguna condición
    });
  };

  // OBTENER LA ÚLTIMA REVISIÓN COMPLETA DEL VEHÍCULO (Solo al editar)
  const obtenerUltimaRevision = async () => {
    // Validar y formatear fechaUltimaRevision
    const fechaMoment = dayjs(fechaUltimaRevision, "DD-MM-YYYY HH:mm:ss"); // `true` para validación estricta

    if (!fechaMoment.isValid()) {
      console.error("Formato de fecha inválido:", fechaUltimaRevision);
      return; // Salir si la fecha no es válida
    }

    const fechaSoloDiaMesAnio = fechaMoment.format("DD-MM-YYYY");

    try {
      const response = await fetch(
        `${UrlApi}/vehiculos_revision/revision_por_fecha/${vehiculo.id}/${fechaSoloDiaMesAnio}`
      );
      const data = await response.json();
      setUltimaRevision(data);
    } catch (error) {
      console.error("Error obteniendo los datos de la revisión:", error);
    }
  };

  //  ------------------------

  useEffect(() => {
    // Sólo ejecuto si estoy en modoEdicion y ya obtuve fecha+kilometraje
    if (
      !modoEdicion ||
      fechaUltimaRevision === null ||
      kilometrajeUltimaRevision === null
    ) {
      return;
    }

    const cargarParaEditar = async () => {
      try {
        // Traer todas las tareas posibles para este tipo de vehículo
        const respTodas = await fetch(
          `${UrlApi}/tareas_tipo_vehiculos/tipo_vehiculo/${vehiculo.id_tipo}/vehiculo/${vehiculo.id}`
        );
        const todasLasTareas = await respTodas.json();

        // Filtrar según frecuencia
        const tareasFiltradas = filtrarTareasPorFrecuencia(
          todasLasTareas,
          fechaRevisionAnterior,
          kilometrajeUltimaRevision
        );

        // Obtener lo guardado en el día hoy
        const fechaSoloHoy = dayjs(
          fechaUltimaRevision,
          "DD-MM-YYYY HH:mm:ss"
        ).format("DD-MM-YYYY");

        const respGuardadas = await fetch(
          `${UrlApi}/vehiculos_revision/revision_por_fecha/${vehiculo.id}/${fechaSoloHoy}`
        );
        const guardadasHoy = await respGuardadas.json();

        // Mapear las guardadas por id_tarea
        const mapaGuardadas = {};
        guardadasHoy.forEach((t) => {
          mapaGuardadas[t.id_tarea] = t;
        });

        // Fusionar cada tarea filtrada con lo guardado hoy (si existe)
        const merged = tareasFiltradas.map((tarea) => {
          const g = mapaGuardadas[tarea.id_tarea];
          if (g) {
            return {
              ...tarea,
              aprobado: g.aprobado,
              observacion: g.observacion,
              id_revision: g.id_revision,
              uuid_revision: g.uuid_revision,
            };
          } else {
            return {
              ...tarea,
              aprobado: 0,
              observacion: "",
              id_revision: null,
              uuid_revision: null,
            };
          }
        });

        setTareasXtipo(merged);
      } catch (error) {
        console.error("Error cargando datos para editar:", error);
      }
    };

    cargarParaEditar();
  }, [modoEdicion, fechaRevisionAnterior, kilometrajeUltimaRevision]);

  // Agrupar tareas según el campo "parte_vehiculo"
  useEffect(() => {
    const agruparTareas = (tareas) => {
      return tareas.reduce((acc, tarea) => {
        const parte = tarea.parte_vehiculo || "Otros";
        if (!acc[parte]) acc[parte] = [];
        acc[parte].push(tarea);
        return acc;
      }, {});
    };

    setTareasAgrupadas(agruparTareas(tareasXtipo));
  }, [tareasXtipo]);

  //  ************************************************************************

  const [uuid_revision, setUuid_revision] = useState(null);

  useEffect(() => {
    if (modoEdicion && ultimaRevision) {
      // En MODO EDICIÓN, usa el uuid de la última revisión
      setUuid_revision(ultimaRevision[0]?.uuid_revision || null);
    } else if (!modoEdicion) {
      // Si no está en MODO EDICIÓN, se toma el nuevo UUID
      setUuid_revision(props.newUuid);
    }
  }, [modoEdicion, ultimaRevision, props.newUuid]);

  //  ------------------------

  //Snackbar Notificacion
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setDisBtn(false);
    setDisBtnGuardar(false);
    setOpenSnack(false);

    props.obtenerVehiculos(); // Actualizo la tabla del componente principal
    handleClose(); // Cierro este componente
  };

  //  ------------------------

  // FECHA DE HOY (PARA GUARDAR)
  const fechaActual = dayjs().format("YYYY-MM-DD HH:mm:ss");

  // GUARDADO PARA CREAR Y EDITAR
  const handleGuardar = async (e) => {
    e.preventDefault();

    // Deshabilitar temporalmente el botón para evitar múltiples clics
    setDisBtn(true);
    setDisBtnGuardar(true);

    try {
      // Filtrar para que solo se guarden las tareas cuya parte esté activa
      const tareasParaGuardar = tareasXtipo.filter(
        (tarea) => enabledCategories[tarea.parte_vehiculo]
      );

      const tareasPromises = tareasParaGuardar.map(async (tarea) => {
        const esEdicion = modoEdicion && tarea.id_revision;

        const url = esEdicion
          ? `${UrlApi}/vehiculos_revision/${tarea.id_revision}` // Para editar
          : `${UrlApi}/vehiculos_revision/`; // Para crear nuevas

        const method = esEdicion ? "PUT" : "POST";

        // Construir bodyData según el contexto (crear o editar)
        const bodyData = esEdicion
          ? {
              // EDITAR
              aprobado: tarea.aprobado || 0,

              // Se asigna el valor de la observación de la tarea
              observacion:
                observacionTemp[tarea.id_tarea]?.trim() !== ""
                  ? observacionTemp[tarea.id_tarea] // Si en 'observacionTemp' hay un texto no vacío para esta tarea, se usa ese
                  : tarea.observacion ?? null, // Si no, se usa la observación original de la tarea ('tarea.observacion'), si tampoco hay una observación original, se asigna 'null'.
              fecha: fechaActual,
              kilometraje: kilometrajeUltimaRevision,
              uuid_revision: tarea.uuid_revision ?? uuid_revision,
              usuario: localStorage.getItem("user"),
            }
          : {
              // CREAR
              id_vehiculo: vehiculo.id,
              id_tarea: tarea.id_tarea,
              aprobado: tarea.aprobado || 0,
              observacion:
                observacionTemp[tarea.id_tarea]?.trim() !== ""
                  ? observacionTemp[tarea.id_tarea]
                  : null,
              kilometraje: modoEdicion
                ? kilometrajeUltimaRevision // Usar km anterior en edición
                : kilometraje, // Usar km ingresado en creación

              fecha: fechaActual,
              uuid_revision: uuid_revision,
              usuario: localStorage.getItem("user"),
            };
        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bodyData),
        });

        if (!response.ok) {
          throw new Error("Error al guardar la tarea");
        }
      });

      // Esperar a que todas las tareas se guarden
      await Promise.all(tareasPromises);

      // Mostrar notificación de éxito
      setOpenSnack(true);
      setColorMensaje("#D4EFDF");
      setMensaje("Tareas guardadas correctamente");
    } catch (error) {
      console.error("Error al guardar las tareas:", error);
      setOpenSnack(true);
      setColorMensaje("#F8D7DA");
      setMensaje("Error al guardar las tareas");
    }
  };

  const handleClose = () => {
    props.onClose(); // Cerrar el diálogo
  };

  return (
    <Dialog fullScreen open={props.open} onClose={props.onClose}>
      <AppBar
        sx={{
          position: "relative",
          boxShadow: 1,
        }}
      >
        <Toolbar sx={{ display: "flex", alignItems: "center" }}>
          {/* Botón para cerrar */}
          <IconButton
            edge="start"
            color="inherit"
            onClick={
              Object.keys(tareasAgrupadas).length > 0
                ? handleReturnClick
                : handleClose
            }
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          {/* Título y última revisión */}
          <Box sx={{ ml: 2, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Revisión de Vehículo
              </Typography>

              {/* Info del vehículo */}
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                sx={{ mt: 0.5 }}
              >
                <Chip
                  label={`Modelo: ${vehiculo.modelo || "No disponible"}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "#2B76A6",
                    bgcolor: "white",
                    fontWeight: 500,
                    fontSize: 14,
                    height: 28,
                    "& .MuiChip-label": {
                      px: 2, // padding horizontal
                    },
                  }}
                />
                <Chip
                  label={`Patente: ${vehiculo.patente}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "#2B76A6",
                    bgcolor: "white",
                    fontWeight: 500,
                    fontSize: 14,
                    height: 28,
                    "& .MuiChip-label": {
                      px: 2, // padding horizontal
                    },
                  }}
                />
                <Chip
                  label={`Kilometraje: ${
                    modoEdicion
                      ? kilometrajeUltimaRevision ?? "No disponible"
                      : kilometraje ?? "No disponible"
                  } km`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "#2B76A6",
                    bgcolor: "white",
                    fontWeight: 500,
                    fontSize: 14,
                    height: 28,
                    "& .MuiChip-label": {
                      px: 2, // padding horizontal
                    },
                  }}
                />
              </Stack>

              {/* Última revisión */}
              {fechaUltimaRevision ? (
                <Typography variant="body2" sx={{ fontStyle: "italic", mt: 1 }}>
                  <b>Última Revisión:</b>{" "}
                  {dayjs(fechaUltimaRevision, "DD-MM-YYYY HH:mm:ss").format(
                    "DD/MM/YYYY, HH:mm"
                  )}{" "}
                  ({dayjs(fechaUltimaRevision, "DD-MM-YYYY HH:mm:ss").fromNow()}
                  )
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: "italic", mt: 1 }}>
                  <b>Última Revisión:</b> No hay registros previos
                </Typography>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <DialogContent>
        {loading ? ( // Muestra el mensaje de carga mientras se están obteniendo las tareas
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              border: "1px solid gray",
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography variant="h6" color="textSecondary">
              Cargando tareas...
            </Typography>
          </Box>
        ) : Object.keys(tareasAgrupadas).length === 0 ? ( // Muestra el mensaje solo si ya se cargaron y están vacías
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              border: "1px solid gray",
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography variant="h6" color="textSecondary">
              {kilometrajeConfirmado
                ? "No hay tareas que coincidan con este vehículo."
                : "Por favor, confirme el kilometraje para cargar las tareas."}
            </Typography>
          </Box>
        ) : (
          // Mostrar tareas si existen
          <Box
            sx={{
              border: "2px solid gray",
              borderRadius: "8px",
              p: 2,
              mb: 2,
            }}
          >
            {/* Título "Tareas" */}
            <Box
              sx={{
                backgroundColor: "#2B76A6",
                borderRadius: "8px 8px 0px 0px",
                p: 1,
                border: "1px solid #05519eff",
                display: "flex", // fila
                alignItems: "center", // centra verticalmente
                justifyContent: "center", // centra todo horizontalmente dentro del box
                gap: 1, // espacio entre ícono y texto
              }}
            >
              <AssignmentIcon sx={{ color: "#ffffffff", fontSize: "1.8rem" }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: "#ffffffff",
                }}
              >
                Tareas
              </Typography>
            </Box>

            {/* Lista de Tareas Agrupadas */}
            <List>
              {Object.entries(tareasAgrupadas).map(([parte, tareas]) => {
                const todasAprobadas = tareas.every(
                  (tarea) => tarea.aprobado === 1
                );

                return (
                  <React.Fragment key={parte}>
                    <Box
                      key={parte}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 1,
                        mb: 1,
                      }}
                    >
                      <Checkbox
                        checked={!!enabledCategories[parte]}
                        onChange={() => toggleCategory(parte)}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 28, // Tamaño del Check más grande
                          },
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          flexGrow: 1,
                          color: enabledCategories[parte] // LETRA
                            ? todasAprobadas
                              ? "#ffffffff" // Todo aprobado
                              : "#ffffffff" // Activada
                            : "#8b8b8bff", // Desactivada
                          backgroundColor: enabledCategories[parte] // FONDO
                            ? todasAprobadas
                              ? "#4e834eff" // Todo aprobado
                              : "#1976D2" // Activada
                            : "#cececed0", // Desactivada
                          p: 1,
                          borderRadius: "6px",
                          textAlign: "center",
                          border: "1px solid",
                          borderColor: enabledCategories[parte] // BORDE
                            ? todasAprobadas
                              ? "#3f9944ff" // Todo aprobado
                              : "#3e77afff" // Activada
                            : "#cfcfcfff", // Desactivada
                        }}
                      >
                        {parte}
                      </Typography>
                    </Box>

                    {tareas.map((tarea) => (
                      <ListItem key={tarea.id_tarea} disablePadding dense>
                        <Paper
                          elevation={0}
                          sx={{
                            width: "100%",
                            mb: 0.5,
                            p: 0.5,
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              tarea.aprobado === 1 ? "#e0f7e0" : "#fafafa",
                            border: "1px solid #ddd",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          {/* Checkbox */}
                          <Checkbox
                            checked={tarea.aprobado === 1}
                            onChange={() =>
                              handleCheckboxChange(tarea.id_tarea)
                            }
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 22 }, // Icono más pequeño
                              color: tarea.aprobado === 1 ? "green" : "default",
                            }}
                            disabled={!enabledCategories[parte]}
                          />

                          {/* Contenido de la tarea */}
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight:
                                  tarea.estado === "completada"
                                    ? "bold"
                                    : "normal",
                                color: !enabledCategories[parte]
                                  ? "#a5a5a5ff"
                                  : "#000",
                                flex: 1,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflow: "visible",
                              }}
                            >
                              {tarea.tarea}
                            </Typography>

                            {/* TextField de observación */}
                            <TextField
                              key={tarea.id_tarea}
                              value={
                                observacionTemp[tarea.id_tarea] ??
                                tarea.observacion ??
                                ""
                              }
                              onChange={(e) =>
                                handleTempObservacionChange(
                                  tarea.id_tarea,
                                  e.target.value
                                )
                              }
                              disabled={
                                tarea.aprobado === 1 ||
                                !enabledCategories[parte]
                              }
                              sx={{
                                width: "55%",
                                "& .MuiInputBase-input": {
                                  py: 0.6,
                                  fontSize: "0.95rem",
                                },
                              }}
                              placeholder="Observación"
                              size="small"
                              autoComplete="off"
                              inputProps={{
                                maxLength: 120, // Limita a 120 caracteres
                              }}
                              helperText={
                                observacionTemp[tarea.id_tarea]?.length >= 100
                                  ? `Te quedan ${
                                      120 -
                                      observacionTemp[tarea.id_tarea]?.length
                                    } caracteres.`
                                  : ""
                              }
                              error={
                                observacionTemp[tarea.id_tarea]?.length >= 110
                              }
                              FormHelperTextProps={{
                                sx: {
                                  fontSize: "0.75rem",
                                  marginTop: "-1px",
                                },
                              }}
                            />
                          </Box>
                        </Paper>
                      </ListItem>
                    ))}
                  </React.Fragment>
                );
              })}
            </List>

            {Object.keys(tareasAgrupadas).length > 0 && (
              <Grid
                container
                direction={{ xs: "column", sm: "row" }}
                sx={{ mt: 1 }}
                spacing={2}
              >
                <Grid item xs={12} sm={5}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={disBtn}
                    onClick={handleReturnClick}
                    sx={{ width: "200px" }}
                  >
                    Cancelar
                  </Button>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Button
                    onClick={() => setOpenCloseAdj(!openCloseAdj)}
                    variant="contained"
                    color="secondary"
                    size="small"
                    fullWidth
                    startIcon={<AttachFileIcon />}
                    disabled={disBtn}
                  >
                    Adjuntar
                  </Button>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGuardar}
                    disabled={isGuardarDisabled || disBtn}
                    fullWidth
                    size="small"
                  >
                    Guardar
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Dialog para colocar el kilometraje inicial */}
      <Dialog
        fullWidth
        maxWidth={"xs"}
        PaperProps={{
          sx: {
            border: "2px solid #1976D2",
            borderRadius: 2, // Bordes redondeados opcionalmente
          },
        }}
        open={dialogoKilometraje}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            // Al presionar ESCAPE o Click a la zona sin dialog no hace nada, evita el cierre del diálogo
          } else {
            handleCloseDialogKilometraje(event, reason); // Ejecuta si el motivo no es backdrop o escape
          }
        }}
      >
        <KilometrajeTareasRevision
          onConfirm={handleConfirmKilometraje}
          onClose={handleCloseDialogKilometraje}
          onClose2={handleCloseBothDialogs}
          kilometrajeInicial={kilometraje}
          kilometrajeUltimaRevision={kilometrajeUltimaRevision}
        />
      </Dialog>

      {openCloseAdj ? ( // ADJUNTAR
        <FileUploadTareas
          fileId={uuid_revision}
          openclose={true}
          mostrar={true}
          onClose={() => handleOpenCloseAdj(false)} // Proporciona una función de cierre
        />
      ) : null}

      {/* Dialogo de confirmación de salida para volver al menú principal */}
      <Dialog
        open={confirmarVolverDialogOpen}
        onClose={handleCloseConfirmarVolverDialog}
        PaperProps={{
          sx: {
            border: "2px solid #2B76A6",
            borderRadius: 2,
            boxShadow: 6,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            fontSize: "1rem",
            bgcolor: "rgba(43,118,166,0.05)",
            borderBottom: "1px solid #eee",
            py: 0.8, // Padding vertical
          }}
        >
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography
            variant="h6"
            fontWeight={600}
            textAlign="center"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box component="span" color="warning.main">
              AVISO
            </Box>
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", mt: 2 }}>
          <Typography>
            Los cambios realizados <b>no se guardarán</b>
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ mt: 2, fontWeight: 500 }}>
            ¿Desea salir sin guardar los cambios?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "center", gap: 3, pb: 2, ml: 2, mr: 2 }}
        >
          <Button
            onClick={handleCloseConfirmarVolverDialog}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2, px: 3 }}
          >
            No, Volver
          </Button>
          <Button
            onClick={handleConfirmarVolverReturn}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Sí, Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de confirmación al deshabilitar */}
      <Dialog
        open={openDisableDialog}
        onClose={handleCancelDisable}
        PaperProps={{
          sx: {
            border: "2px solid #2B76A6",
            borderRadius: 2,
            boxShadow: 6,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            fontSize: "1rem",
            bgcolor: "rgba(43,118,166,0.05)",
            borderBottom: "1px solid #eee",
            py: 0.8, // Padding vertical
          }}
        >
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography
            variant="h6"
            fontWeight={600}
            textAlign="center"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box component="span" color="warning.main">
              AVISO
            </Box>
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", mt: 2 }}>
          <Typography>
            Va a deshabilitar la parte <b>{pendingCategory}</b>.
          </Typography>
          <Typography>
            Si existen datos en sus tareas, estos se borrarán.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ mt: 2, fontWeight: 500 }}>
            ¿Desea continuar con la desactivación?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button
            onClick={handleCancelDisable}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2, px: 3 }}
          >
            No, volver
          </Button>
          <Button
            onClick={handleConfirmDisable}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Sí, desactivar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MENSAJE AL GUARDAR */}
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          open={openSnack}
          autoHideDuration={1500}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnack}
            severity="success"
            sx={{ width: "100%" }}
            style={{ backgroundColor: colormensaje, color: "black" }}
          >
            {mensaje}
          </Alert>
        </Snackbar>
      </Stack>
    </Dialog>
  );
}

export default TareasRevision;
